import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as _ from 'lodash';
import { SelectInputField, SelectInputFieldProps } from 'features/app/components/SelectInputField';
import { AxiosPromise } from 'axios';
import LoadingMessage from 'features/app/components/SelectInput/LoadingMessage';
import LoadingIndicator from 'features/app/components/SelectInput/LoadingIndicator';
import { PaginatedResponse } from 'features/app/ts/PaginatedResponse';
import { InputActionTypes } from 'react-select';

type SelectInputScrollLoadingFieldProps<T> = Omit<
  SelectInputFieldProps<T>,
  'menuListComponent' | 'isAsync' | 'fetchItems' | 'options'
> & {
  fetchItems: (props: { page_size: number; page: number; searchValue: string }) => AxiosPromise;
  transformRequestResponse?: (
    props: Array<Record<string, string | number>>
  ) => Array<{ label: string | number; value?: string | number | boolean | null }>;
};

const SelectInputScrollLoadingField = <T,>({
  fetchItems,
  transformRequestResponse,
  ...props
}: SelectInputScrollLoadingFieldProps<T>) => {
  const requestParamsInitial = useMemo(
    () => ({
      page_size: 15,
      page: 1,
      searchValue: '',
    }),
    []
  );
  const [data, setData] = useState<PaginatedResponse<Record<string, string | number>>>();
  const [showLoadingIndicator, setShowLoadingIndicator] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [scrollDownCounter, setScrollDownCounter] = useState<number>(0);
  const [requestParams, setRequestParams] =
    useState<{ page_size: number; page: number; searchValue: string }>(requestParamsInitial);

  const fetchData = useCallback(() => {
    setIsFetching(true);
    return fetchItems(requestParams).then(res => {
      const responseData = res.data;
      setIsFetching(false);
      setShowLoadingIndicator(false);

      if (responseData && !responseData?.results) {
        throw Error('data from API should have the "results" field!');
      }
      if (responseData) {
        setData(state => ({ ...responseData, results: [...(state?.results || []), ...responseData.results] }));
      }
    });
  }, [requestParams, fetchItems]);

  const getOptions = useCallback(() => {
    if (transformRequestResponse && data) {
      return transformRequestResponse(data.results);
    } else if (data) {
      return data.results.map(({ id, name }) => ({ value: id, label: name }));
    }
    return [];
  }, [transformRequestResponse, data]);

  const options = useMemo(() => getOptions(), [getOptions]);

  const handleInputChange = useCallback(
    (value: string, { action }: { action: InputActionTypes }) => {
      if (['set-value', 'input-blur'].includes(action)) return;
      if (data) setData({ ...data, results: [] });
      setRequestParams({ ...requestParamsInitial, searchValue: value || '' });
    },
    [requestParamsInitial, data]
  );

  const debounceSearch = useMemo(
    () => _.debounce((value, action) => handleInputChange(value, action), 400),
    [handleInputChange]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (scrollDownCounter === 0) return;
    if (!data?.next && !isFetching) return;
    setRequestParams(state => ({ ...state, page: state.page + 1 }));
    setShowLoadingIndicator(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollDownCounter]);

  return (
    <SelectInputField
      {...props}
      options={options}
      onInputChange={debounceSearch}
      isLoading={isFetching}
      filterOption={() => true}
      onMenuScrollToBottom={() => setScrollDownCounter(state => state + 1)}
      components={
        {
          LoadingMessage,
          LoadingIndicator: showLoadingIndicator ? LoadingIndicator : null,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any
      }
    />
  );
};

export default SelectInputScrollLoadingField;
